import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Play } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PROJECTS, FILTERS } from "@/data/content";

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible = useMemo(
    () => (filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <div data-testid="gallery-page" className="pt-20">
      <Seo
        title="Flooring Project Gallery — COC Flooring Ltd"
        description="Browse real COC Flooring projects: herringbone hardwood, kitchen LVT, commercial resin floors and staircase work, filterable by floor type."
      />
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
              Portfolio
            </p>
          </MaskedLine>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <MaskedLine delay={0.2}>Work that speaks</MaskedLine>
            <MaskedLine delay={0.32}>
              <em className="italic font-light text-primary">in grain &amp; grout.</em>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.45}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="gallery-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={f.testid}
                  onClick={() => setFilter(f.id)}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                    filter === f.id
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </MaskedLine>
        </div>
      </section>

      <section className="pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[260px]">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.button
                  layout
                  key={p.id}
                  type="button"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setActive(p)}
                  data-testid="gallery-project-card"
                  className={`group relative overflow-hidden rounded-xl text-left ${p.span}`}
                >
                  {p.type === "video" ? (
                    <video
                      src={p.url}
                      muted
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <img
                      src={p.url}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  )}
                  {p.type === "video" && (
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-[#FAF8F5] font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                      <Play size={10} fill="currentColor" /> Video
                    </span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E9C093]">
                      {p.category}
                    </p>
                    <h3 className="mt-2 font-serif text-lg sm:text-xl text-[#FAF8F5]">
                      {p.title}
                    </h3>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-[#A39E93]">
                      <MapPin size={12} /> {p.location} — {p.year}
                    </p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {active.type === "video" ? (
                <video
                  src={active.url}
                  controls
                  autoPlay
                  className="w-full max-h-[75vh] rounded-xl bg-black"
                />
              ) : (
                <img
                  src={active.url}
                  alt={active.title}
                  className="w-full max-h-[75vh] object-cover rounded-xl"
                />
              )}
              <figcaption className="mt-4 flex items-center justify-between text-[#FAF8F5]">
                <div>
                  <div className="font-serif text-xl">{active.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mt-1">
                    {active.category} — {active.location}, {active.year}
                  </div>
                </div>
                <button
                  data-testid="lightbox-close-button"
                  onClick={() => setActive(null)}
                  aria-label="Close"
                  className="w-11 h-11 rounded-full border border-[#FAF8F5]/30 flex items-center justify-center hover:bg-[#C87D32] hover:border-[#C87D32] transition-colors"
                >
                  <X size={18} />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
